/*
 * Feature flags: the Bindings tab of the Feature Detail modal (audit Phase 3.3).
 * Replaces wwwroot/js/Feature/Bindings.js. No jQuery.
 *
 * One binding is a stated on/off for one target at the feature's level; a
 * target with no row gets the feature's default. Every write is an upsert by
 * target (PUT Feature/{id}/Binding) or a delete, and then the tab redraws
 * itself -- the row, its state badge and the count on the tab header all come
 * from the one endpoint, so there is nothing to patch by hand afterwards.
 */
import { ajaxJson, failToast } from '../core/ajax';
import { one } from '../core/dom';
import { confirm } from '../core/modal';
import { toast } from '../core/toast';
import {
    BINDINGS_PANE, markListDirty, reloadTab, setTabBadge,
} from './feature';

/** The window an add carries: both ends, empty meaning "no bound at this end". */
interface EffectiveWindow {
    from: string;
    until: string;
}

function value(id: string): string {
    return (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
}

function number(id: string): number {
    const parsed = Number.parseInt(value(id), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export const Bindings = {
    /**
     * Bind whatever the picker row names. The single picker is a person for a
     * Person-level feature and a resource (domain / application / tenant)
     * otherwise; the group picker is rendered only at Person level, and its
     * presence is what says which of the two the id means.
     */
    Add(featureId: number | string): void {
        const targetId = number(`FeatureBindTarget_${featureId}`);
        const groupId = number(`FeatureBindGroup_${featureId}`);
        if (!targetId && !groupId) {
            toast('warning', 'Pick a target', 'Use the search button to pick what this binding is for.');
            return;
        }
        if (targetId && groupId) {
            toast('warning', 'One target', 'Pick either a person or a person group, not both.');
            return;
        }
        const isPersonLevel = document.getElementById(`FeatureBindGroup_${featureId}`) !== null;
        const enabled = (document.getElementById(`FeatureBindEnabled_${featureId}`) as HTMLInputElement | null)?.checked === true;
        upsert(featureId, {
            resourceId: isPersonLevel ? 0 : targetId,
            personId: isPersonLevel ? targetId : 0,
            groupId,
            enabled,
            notes: value(`FeatureBindNotes_${featureId}`),
            // The window travels only from the Bind form. A flip sends none, and
            // the server leaves an existing binding's window alone when it does.
            window: { from: value(`FeatureBindFrom_${featureId}`), until: value(`FeatureBindUntil_${featureId}`) },
            done: enabled ? 'Bound on' : 'Bound off',
        });
    },

    /** The state badge on a row: flip it to the other state, keeping notes and window. */
    Flip(
        featureId: number | string,
        resourceId: unknown,
        personId: unknown,
        groupId: unknown,
        enabled: unknown,
    ): void {
        const now = enabled !== true;
        upsert(featureId, {
            resourceId: Number(resourceId) || 0,
            personId: Number(personId) || 0,
            groupId: Number(groupId) || 0,
            enabled: now,
            done: now ? 'Switched on' : 'Switched off',
        });
    },

    Remove(featureId: number | string, bindingId: number): void {
        void confirm({
            title: 'Remove binding',
            html: "Remove this binding? The target reverts to the feature's default.",
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Feature/${featureId}/Binding/${bindingId}`,
                    method: 'DELETE',
                    alertInto: `FeatureBindingsMessageContainer_${featureId}`,
                    errorTitle: 'Failed to remove binding',
                    onSuccess: () => {
                        toast('success', 'Binding removed');
                        void Bindings.Reload();
                    },
                    onFailure: failToast('Failed to remove binding'),
                });
            },
        });
    },

    /**
     * Redraw the tab, then put the header count back in step.
     *
     * The count and the list row behind the modal were both rendered when the
     * modal opened. The badge can be fixed here; the row cannot, because the
     * list contains the modal -- so the list is marked stale and redrawn when
     * the dialog closes.
     */
    Reload(): Promise<void> {
        return reloadTab(BINDINGS_PANE).then(() => {
            setTabBadge(BINDINGS_PANE, one(BINDINGS_PANE)?.querySelectorAll('tr[data-bindingid]').length ?? 0);
            markListDirty();
        });
    },
};

interface UpsertOptions {
    resourceId: number;
    personId: number;
    groupId: number;
    enabled: boolean;
    notes?: string;
    window?: EffectiveWindow;
    done: string;
}

function upsert(featureId: number | string, options: UpsertOptions): void {
    const data: Record<string, string> = {
        TargetResourceId: options.resourceId ? String(options.resourceId) : '',
        TargetPersonId: options.personId ? String(options.personId) : '',
        TargetPersonGroupId: options.groupId ? String(options.groupId) : '',
        Enabled: options.enabled ? 'true' : 'false',
    };
    // Absent notes keep whatever the binding already carries; an empty string clears them.
    if (options.notes !== undefined) data['Notes'] = options.notes;
    if (options.window) {
        data['WindowSpecified'] = 'true';
        data['EffectiveFrom'] = options.window.from;
        data['EffectiveUntil'] = options.window.until;
    }
    void ajaxJson({
        url: `Feature/${featureId}/Binding`,
        method: 'PUT',
        data,
        alertInto: `FeatureBindingsMessageContainer_${featureId}`,
        errorTitle: 'Failed to bind',
        onSuccess: () => {
            toast('success', options.done);
            void Bindings.Reload();
        },
        onFailure: failToast('Failed to bind'),
    });
}
